import React, { useEffect, useMemo, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Tamaño de los pines (1 = tamaño base). Baja este valor para hacerlos más pequeños.
const PIN_SCALE = 0.7;

// Coordenadas de capitales y nombres comunes
const CITY_COORDS = {
  "Sydney":            [-33.8688, 151.2093],
  "Greater Sydney":    [-33.8688, 151.2093],
  "Melbourne":         [-37.8136, 144.9631],
  "Greater Melbourne": [-37.8136, 144.9631],
  "Brisbane":          [-27.4698, 153.0251],
  "Greater Brisbane":  [-27.4698, 153.0251],
  "Adelaide":          [-34.9285, 138.6007],
  "Greater Adelaide":  [-34.9285, 138.6007],
  "Perth":             [-31.9523, 115.8613],
  "Greater Perth":     [-31.9523, 115.8613],
  "Hobart":            [-42.8821, 147.3272],
  "Greater Hobart":    [-42.8821, 147.3272],
  "Darwin":            [-12.4634, 130.8456],
  "Canberra":          [-35.2809, 149.1300],
  "Australian Capital Territory": [-35.2809, 149.1300],
};

// Bounds aproximados de Australia
const AU_BOUNDS = L.latLngBounds([[-44, 112], [-9, 154]]);

// Icono tipo “pin de Google” (SVG) con escala
function googleLikePin(color = "#3366cc", scale = PIN_SCALE) {
  const baseW = 28, baseH = 40;     // tamaño base del SVG
  const w = Math.round(baseW * scale);
  const h = Math.round(baseH * scale);

  const svg = `
    <svg width="${w}" height="${h}" viewBox="0 0 ${baseW} ${baseH}" xmlns="http://www.w3.org/2000/svg"
         style="display:block;filter:drop-shadow(0 1px 2px rgba(0,0,0,.35))">
      <path d="M14 0C6.268 0 0 6.268 0 14c0 10.5 14 26 14 26s14-15.5 14-26C28 6.268 21.732 0 14 0z"
            fill="${color}"/>
      <circle cx="14" cy="14" r="6" fill="white"/>
    </svg>`;

  return L.divIcon({
    className: "gpin",
    html: svg,
    iconSize: [w, h],
    iconAnchor: [w / 2, h],          // punta del pin
    popupAnchor: [0, -0.85 * h],
    tooltipAnchor: [w / 2, -0.45 * h],
  });
}

export default function CityMapLegend({ selectedCities = [], colorMap = {} }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersLayerRef = useRef(null);

  const citiesWithCoords = useMemo(
    () =>
      selectedCities
        .map((name) => ({ name, coords: CITY_COORDS[name] }))
        .filter((x) => Array.isArray(x.coords)),
    [selectedCities]
  );

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    const map = L.map(mapRef.current, {
      zoomControl: true,
      attributionControl: true,
      scrollWheelZoom: true,
    });

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap contributors",
      maxZoom: 19,
    }).addTo(map);

    markersLayerRef.current = L.layerGroup().addTo(map);

    if (citiesWithCoords.length) {
      const b = L.latLngBounds(citiesWithCoords.map((c) => c.coords));
      map.fitBounds(b.pad(0.25));
    } else {
      map.fitBounds(AU_BOUNDS);
    }

    mapInstanceRef.current = map;
  }, [citiesWithCoords]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    const layer = markersLayerRef.current;
    if (!map || !layer) return;

    layer.clearLayers();

    if (citiesWithCoords.length) {
      citiesWithCoords.forEach(({ name, coords }) => {
        const color = colorMap[name] || "#3366cc";
        L.marker(coords, { icon: googleLikePin(color) })
          .bindTooltip(name, { direction: "top", offset: [0, -8 * PIN_SCALE], sticky: true })
          .addTo(layer);
      });

      const b = L.latLngBounds(citiesWithCoords.map((c) => c.coords));
      map.fitBounds(b.pad(0.25));
    } else {
      map.fitBounds(AU_BOUNDS);
    }
  }, [citiesWithCoords, colorMap]);

  return (
    <div className="city-map-legend-container">
      <div className="city-map-svg-wrapper">
        {/* Dentro del wrapper original, renderizamos el mapa */}
        <div ref={mapRef} style={{ width: "100%", height: "100%", borderRadius: 12 }} />
      </div>

      <div className="city-legend-list">
        {selectedCities.map((name) => (
          <div className="city-legend-item" key={name}>
            <span
              className="city-legend-color"
              style={{ background: colorMap[name] || "#3366cc" }}
            />
            <span>{name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
